import React, { Component } from 'react';
import {
    Dialog, DialogContent, Typography, Button, Divider, IconButton, TextField, CircularProgress
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GppMaybeIcon from '@material-ui/icons/Security';
import EmailIcon from '@material-ui/icons/Email';

const DANGER_COLOR = '#b71c1c';

const styles = {
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 24px 12px',
        backgroundColor: '#fff0f0',
    },
    content: {
        padding: '16px 24px 24px',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 8,
        padding: '12px 24px 16px',
        backgroundColor: '#f5f5f5',
        borderTop: '1px solid #e0e0e0',
    },
    warningBox: {
        backgroundColor: '#ffebee',
        border: '2px solid #ef5350',
        borderLeft: '6px solid #b71c1c',
        borderRadius: 6,
        padding: '12px 16px',
        marginBottom: 16,
        color: '#b71c1c',
        fontSize: 13,
        lineHeight: 1.5,
    },
    sectionLabel: {
        color: '#424242',
        marginBottom: 6,
        marginTop: 14,
        fontWeight: 600,
        fontSize: 13,
    },
    confirmBtn: (disabled) => ({
        backgroundColor: disabled ? '#bdbdbd' : DANGER_COLOR,
        color: '#fff',
        textTransform: 'none',
        fontWeight: 700,
        padding: '10px 32px',
        borderRadius: 6,
        boxShadow: disabled ? 'none' : '0 2px 12px rgba(183,28,28,0.4)',
        cursor: disabled ? 'not-allowed' : 'pointer',
    }),
};

/**
 * Dialog ultra-destrutivo para "Forçar drop imediato".
 * Requer: email 2FA enviado/reenviado, workspace digitado, frase `DELETAR BANCO DE DADOS {workspace}`,
 * motivo (min 20 chars), código 2FA (6 dígitos).
 *
 * Props:
 *  - open, onClose, workspace, defaultEmail
 *  - onSendCode({ email }) — dispara envio (parent retorna { sent, expiresAt, resendsRemaining, errorMessage })
 *  - onConfirm(payload) — parent executa o drop
 *  - codeSentState: { sent, expiresAt, resendsRemaining, errorMessage } retornado por onSendCode
 *  - sending: bool (durante envio do código)
 *  - submitting: bool (durante execução do drop)
 */
class ForceDropConfirmDialog extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // email NÃO é mais editável — a fonte da verdade é props.defaultEmail (vem do
            // userSession.user.email). Backend trava: ignora o que vier do body e usa o JWT.
            // Mantemos só os campos que o usuário realmente preenche.
            typedWorkspace: '',
            typedPhrase: '',
            reason: '',
            twoFactorCode: '',
            remainingSeconds: 0,
        };
        this._timerId = null;
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.open && this.props.open) {
            this.setState({
                typedWorkspace: '',
                typedPhrase: '',
                reason: '',
                twoFactorCode: '',
                remainingSeconds: 0,
            });
        }
        if (this.props.codeSentState?.expiresat && this.props.codeSentState.expiresat !== prevProps.codeSentState?.expiresat) {
            this.startTimer(this.props.codeSentState.expiresat);
        }
    }

    componentWillUnmount() { this.stopTimer(); }

    startTimer = (expiresAt) => {
        this.stopTimer();
        const tick = () => {
            const secs = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
            this.setState({ remainingSeconds: secs });
            if (secs <= 0) this.stopTimer();
        };
        tick();
        this._timerId = setInterval(tick, 1000);
    }

    stopTimer = () => {
        if (this._timerId) { clearInterval(this._timerId); this._timerId = null; }
    }

    handleSendCode = () => {
        if (this.props.onSendCode) this.props.onSendCode({ email: this.props.defaultEmail });
    }

    handleConfirm = () => {
        const { typedWorkspace, typedPhrase, reason, twoFactorCode } = this.state;
        if (this.props.onConfirm) {
            this.props.onConfirm({
                confirmWorkspace: typedWorkspace,
                confirmPhrase: typedPhrase,
                reason,
                twoFactorCode,
                targetEmail: this.props.defaultEmail,
            });
        }
    }

    isConfirmDisabled = () => {
        const { typedWorkspace, typedPhrase, reason, twoFactorCode } = this.state;
        const { workspace, submitting, codeSentState } = this.props;
        if (submitting) return true;
        if (!workspace) return true;
        if (typedWorkspace !== workspace) return true;
        const expectedPhrase = `DELETAR BANCO DE DADOS ${workspace}`;
        if (typedPhrase !== expectedPhrase) return true;
        if (reason.trim().length < 20) return true;
        if (!twoFactorCode || twoFactorCode.length < 4) return true;
        if (!codeSentState?.sent) return true;
        return false;
    }

    formatTimer = (secs) => {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    }

    render() {
        const { open, workspace, onClose, sending, submitting, codeSentState, defaultEmail } = this.props;
        const disabled = this.isConfirmDisabled();
        const expectedPhrase = workspace ? `DELETAR BANCO DE DADOS ${workspace}` : '';
        const resendsLeft = codeSentState?.resendsremaining;
        const timerText = this.state.remainingSeconds > 0 ? ` · Expira em ${this.formatTimer(this.state.remainingSeconds)}` : '';

        return (
            <Dialog
                open={open}
                // Modal ultra-destrutivo: só fecha via X ou botão Cancelar.
                // Ignora click no backdrop e ESC pra evitar dismiss acidental durante preenchimento.
                onClose={(_, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                    if (onClose) onClose();
                }}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
                PaperProps={{ style: { borderRadius: 12, overflow: 'hidden' } }}
            >
                <div style={styles.header}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <GppMaybeIcon style={{ fontSize: 30, color: DANGER_COLOR, marginRight: 12 }} />
                        <Typography variant="h6" style={{ fontWeight: 700, fontSize: 18, color: DANGER_COLOR }}>
                            Forçar deleção imediata do banco de dados
                        </Typography>
                    </div>
                    <IconButton size="small" onClick={onClose} style={{ color: '#9e9e9e' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>

                <Divider />

                <DialogContent style={styles.content}>
                    <div style={styles.warningBox}>
                        <strong>🔴 AÇÃO IRREVERSÍVEL — pula TODOS os delays de segurança.</strong><br/>
                        O banco de dados do contratante <strong>{workspace}</strong> será <strong>deletado permanentemente em segundos</strong>.
                        Se o ambiente ainda não foi desprovisionado, infra, gateway e registros SaaS também serão removidos <strong>imediatamente</strong>.
                        Não haverá janela de reversão.
                    </div>

                    <Typography style={styles.sectionLabel}>1. Email do usuário logado (não editável):</Typography>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <TextField
                            value={defaultEmail || ''}
                            variant="outlined"
                            size="small"
                            style={{ flex: 1 }}
                            disabled
                            InputProps={{ readOnly: true }}
                            placeholder="—"
                            helperText="O código 2FA é enviado para o email do admin logado. Backend valida — não aceita outro destinatário."
                        />
                        <Button
                            variant="outlined"
                            startIcon={sending ? <CircularProgress size={16} /> : <EmailIcon />}
                            onClick={this.handleSendCode}
                            disabled={sending || submitting || !defaultEmail}
                            style={{ textTransform: 'none', whiteSpace: 'nowrap', borderColor: DANGER_COLOR, color: DANGER_COLOR, minWidth: 170, paddingLeft: 16, paddingRight: 16 }}
                        >
                            {codeSentState?.sent ? 'Reenviar' : 'Enviar código'}
                        </Button>
                    </div>
                    {codeSentState && codeSentState.sent && (
                        <Typography variant="caption" style={{ color: '#388e3c', marginTop: 4, display: 'block' }}>
                            Código enviado para {codeSentState.targetemail}. {resendsLeft != null ? `Reenvios restantes: ${resendsLeft}.` : ''}{timerText}
                        </Typography>
                    )}
                    {codeSentState && !codeSentState.sent && codeSentState.errormessage && (
                        <Typography variant="caption" style={{ color: '#d32f2f', marginTop: 4, display: 'block' }}>
                            {codeSentState.errormessage}
                        </Typography>
                    )}

                    <Typography style={styles.sectionLabel}>
                        2. Digite o workspace: <strong style={{ color: DANGER_COLOR, fontFamily: 'monospace' }}>{workspace}</strong>
                    </Typography>
                    <TextField
                        value={this.state.typedWorkspace}
                        onChange={(e) => this.setState({ typedWorkspace: e.target.value })}
                        variant="outlined"
                        size="small"
                        fullWidth
                        autoComplete="off"
                        disabled={submitting}
                        inputProps={{ style: { fontFamily: 'monospace' } }}
                    />

                    <Typography style={styles.sectionLabel}>
                        3. Digite a frase de confirmação: <strong style={{ color: DANGER_COLOR, fontFamily: 'monospace' }}>{expectedPhrase}</strong>
                    </Typography>
                    <TextField
                        value={this.state.typedPhrase}
                        onChange={(e) => this.setState({ typedPhrase: e.target.value })}
                        variant="outlined"
                        size="small"
                        fullWidth
                        autoComplete="off"
                        disabled={submitting}
                        inputProps={{ style: { fontFamily: 'monospace' } }}
                    />

                    <Typography style={styles.sectionLabel}>4. Motivo (mínimo 20 caracteres):</Typography>
                    <TextField
                        value={this.state.reason}
                        onChange={(e) => this.setState({ reason: e.target.value })}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        disabled={submitting}
                        helperText={`${this.state.reason.length}/20+ caracteres`}
                    />

                    <Typography style={styles.sectionLabel}>5. Código 2FA recebido por email:</Typography>
                    <TextField
                        value={this.state.twoFactorCode}
                        onChange={(e) => this.setState({ twoFactorCode: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={submitting || !codeSentState?.sent}
                        autoComplete="off"
                        inputProps={{ style: { fontFamily: 'monospace', fontSize: 20, letterSpacing: 6, textAlign: 'center' } }}
                        placeholder="000000"
                    />
                    {this.props.submitError && (
                        <div style={{
                            marginTop: 16,
                            padding: '10px 14px',
                            borderRadius: 6,
                            backgroundColor: '#ffebee',
                            border: '1px solid #ef5350',
                            borderLeft: '4px solid #b71c1c',
                            color: '#b71c1c',
                            fontSize: 13,
                            fontWeight: 500,
                        }}>
                            ⚠ {this.props.submitError}
                        </div>
                    )}
                </DialogContent>

                <div style={styles.footer}>
                    <Button
                        onClick={onClose}
                        variant="outlined"
                        style={{ textTransform: 'none', color: '#616161', padding: '8px 24px' }}
                        disabled={submitting}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={this.handleConfirm}
                        variant="contained"
                        disabled={disabled}
                        style={styles.confirmBtn(disabled)}
                    >
                        {submitting ? 'Executando deleção...' : 'FORÇAR DELEÇÃO IMEDIATA'}
                    </Button>
                </div>
            </Dialog>
        );
    }
}

export default ForceDropConfirmDialog;
